import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { Container } from '../../components/Animations'
import { ButtonLeadingIcon, PrimaryButton, SecondaryButton } from '../../components/Button'
import CopyButton from '../../components/CopyButton'
import CreateDepositAddressModal from '../../components/CreateDepositAddressModal'
import Dropdown, { DropdownContext, DropdownItem, DropdownTextInput } from '../../components/Dropdown'
import {
  EmptyState,
  DashboardPage,
  InlinePill,
  PageActionRow,
  PageHeaderRow,
  Surface,
  SurfaceHeader,
  SurfaceSubtext,
  SurfaceTitle
} from '../../components/DashboardPrimitives'
import LoadingBar from '../../components/LoadingBar'
import Modal, { ModalFooter } from '../../components/Modal'
import PageHeader from '../../components/PageHeader'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import { formatDepositAccountType } from '../../components/Shared'
import AssetWithdrawalModal, { WithdrawableAsset } from '../../components/AssetWithdrawalModal'
import { TextInput } from '../../components/Input'
import ExportDepositsForm from '../../components/ExportDepositsForm'
import ExportWithdrawalsForm from '../../components/ExportWithdrawalsForm'
import Radom, {
  BridgeVirtualAccountCapabilities,
  Deposit,
  DepositAccount,
  DepositAccountType,
  GeneralDepositMethod,
  ManagedBalance,
  WithdrawalAccount,
  WithdrawalRequest
} from '../../state/Radom'
import Treasury from '../../state/Treasury'
import User from '../../state/User'
import { WINDOW_SIZE } from '../../util/Constants'
import { DepositMethods, getMethod, prodNetworks, testNetworks } from '../../util/Managed'
import { formatAssetAmount, formatCurrency, shortAddress, useTestMode } from '../../util/Util'
import CreateWithdrawalAccountModal, { AccountType } from './CreateWithdrawalAccountModal'

type FundingFlow = 'deposit' | 'withdraw'
type FundingRail = 'crypto' | 'bank'
type FundingView = 'overview' | 'deposit' | 'withdraw' | 'accounts'
type AccountManagerTab = 'depositCrypto' | 'depositBank' | 'withdrawCrypto' | 'withdrawBank'
type DepositCashMethod = 'sepaMemo' | 'virtualAccount' | 'openBanking'
type ManagedFundingAccount =
  | { kind: 'deposit', rail: FundingRail, account: DepositAccount }
  | { kind: 'withdraw', rail: FundingRail, account: WithdrawalAccount }

interface FundingBalanceRow {
  source: ManagedBalance
  ticker: string
  displayName: string
  networkLabel: string
  fiatValue: number
  isFiat: boolean
}

interface CryptoDepositOption {
  key: string
  network: string
  token?: string
  accounts: DepositAccount[]
}

interface CryptoDepositAssetOption {
  ticker: string
  name: string
  options: CryptoDepositOption[]
}

const getDepositMethodKey = (network: string, token?: string): string => `${network}:${token ?? 'native'}`

const POPULAR_DEPOSIT_ASSET_ORDER = [
  'USDC',
  'USDT',
  'BTC',
  'ETH',
  'LTC',
  'SOL',
  'DOGE',
  'BCH',
  'BSV',
  'XMR',
  'BNB',
  'MATIC',
  'POL',
  'AVAX',
  'DOT',
  'BAT'
]

const POPULAR_DEPOSIT_NETWORK_ORDER = [
  'Ethereum',
  'Tron',
  'Solana',
  'Bitcoin',
  'Litecoin',
  'Base',
  'Arbitrum',
  'Polygon',
  'BNB',
  'Avalanche',
  'Dogecoin',
  'BitcoinCash',
  'BitcoinSV',
  'Monero',
  'SepoliaTestnet',
  'BitcoinTestnet',
  'LitecoinTestnet'
]

const DEPOSIT_NETWORK_BLOCK_TIMES_SECONDS: Record<string, number> = {
  Arbitrum: 1,
  Avalanche: 2,
  Base: 2,
  Bitcoin: 600,
  BitcoinCash: 600,
  BitcoinCashTestnet: 600,
  BitcoinSV: 600,
  BitcoinTestnet: 600,
  BNB: 3,
  BNBTestnet: 3,
  Dogecoin: 60,
  Ethereum: 12,
  Litecoin: 150,
  LitecoinTestnet: 150,
  Monero: 120,
  MoneroStagenet: 120,
  Polygon: 2,
  PolygonTestnet: 2,
  PolkadotAssetHub: 12,
  PolkadotAssetHubTestnet: 12,
  SepoliaTestnet: 12,
  Solana: 1,
  SolanaDevnet: 1,
  Tron: 3
}

const sortedOrderIndex = (list: string[], value?: string): number => {
  const idx = list.findIndex(v => v.toLowerCase() === (value ?? '').toLowerCase())
  return idx >= 0 ? idx : list.length + 1
}

const formatDepositNetworkArrival = (network?: string): string => {
  const seconds = network ? DEPOSIT_NETWORK_BLOCK_TIMES_SECONDS[network] : undefined
  if (!seconds) {
    return 'Varies'
  }

  if (seconds < 60) {
    return `~ ${seconds} sec`
  }

  const minutes = seconds / 60
  return `~ ${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)} min`
}

const collectDepositMethodLeaves = (node: any): Array<{ network: string, token?: string }> => {
  if (node?.method) {
    return [{
      network: node.method.network,
      token: node.method.token
    }]
  }

  if (Array.isArray(node?.subMethods) && node.subMethods.length > 0) {
    return node.subMethods.flatMap(collectDepositMethodLeaves)
  }

  if (node?.name) {
    return [{
      network: node.name,
      token: undefined
    }]
  }

  return []
}

const SegmentedControl = styled.div`
  align-items: center;
  background: #f4f6fa;
  border: 1px solid #e8edf5;
  border-radius: 12px;
  display: inline-grid;
  gap: 2px;
  grid-template-columns: repeat(2, minmax(110px, 1fr));
  min-width: 240px;
  padding: 3px;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    width: 100%;
  }
`

const SegmentButton = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => $active ? '#ffffff' : 'transparent'};
  border: 0;
  border-radius: 9px;
  box-shadow: ${({ $active }) => $active ? '0 1px 4px rgba(15, 23, 42, 0.08)' : 'none'};
  color: ${({ $active }) => $active ? '#20242d' : '#687183'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12.75px;
  font-weight: 500;
  line-height: 1.2;
  min-height: 32px;
  padding: 7px 12px;
  transition: 0.16s ease all;
  white-space: nowrap;
`

const FundingLayout = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1.25fr) minmax(320px, 0.75fr);

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    grid-template-columns: 1fr;
  }
`

const ActionGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1fr);
`

const OverviewGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    grid-template-columns: 1fr;
  }
`

const OverviewAction = styled(Surface)`
  min-height: 152px;
`

const Workspace = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const CenteredWorkspace = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0 auto;
  max-width: 760px;
  width: 100%;
`

const FundingHero = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const FundingTitle = styled.h1`
  color: #20242d;
  font-size: 20px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    font-size: 18px;
  }
`

const UnderlineTabs = styled.div`
  align-items: flex-end;
  border-bottom: 1px solid #e4e9f1;
  display: flex;
  gap: 20px;
  min-width: 0;
`

const UnderlineTab = styled.button<{ $active?: boolean }>`
  background: transparent;
  border: 0;
  border-bottom: 2px solid ${({ $active }) => $active ? '#20242d' : 'transparent'};
  color: ${({ $active }) => $active ? '#20242d' : '#6c7483'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.2;
  margin-bottom: -1px;
  min-height: 34px;
  padding: 0 0 8px;
  transition: color 0.16s ease, border-color 0.16s ease;
  white-space: nowrap;

  &:hover {
    color: #20242d;
  }
`

const FlowSurface = styled(Surface)`
  gap: 16px;
  padding: 16px;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    padding: 18px;
  }
`

const StepStack = styled.div`
  display: flex;
  flex-direction: column;
`

const StepRow = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 30px minmax(0, 1fr);
  min-width: 0;
`

const StepRail = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding-top: 2px;
`

const StepMarker = styled.div<{ $complete?: boolean, $active?: boolean }>`
  align-items: center;
  background: ${({ $complete, $active }) => $complete || $active ? '#20242d' : '#fff'};
  border: 1px solid ${({ $complete, $active }) => $complete || $active ? '#20242d' : '#d6dce7'};
  border-radius: 999px;
  color: ${({ $complete, $active }) => $complete || $active ? '#fff' : '#7b8494'};
  display: flex;
  flex: 0 0 24px;
  font-size: 12px;
  font-weight: 600;
  height: 24px;
  justify-content: center;
  line-height: 1;
  width: 24px;
`

const StepConnector = styled.div<{ $hidden?: boolean }>`
  background: repeating-linear-gradient(
    to bottom,
    #cfd6e2 0,
    #cfd6e2 4px,
    transparent 4px,
    transparent 8px
  );
  display: ${({ $hidden }) => $hidden ? 'none' : 'block'};
  flex: 1 1 auto;
  margin: 7px 0;
  min-height: 34px;
  width: 1px;
`

const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 0 0 20px;
`

const StepHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  min-width: 0;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    flex-direction: column;
  }
`

const StepTitle = styled.h2`
  color: #20242d;
  font-size: 14.25px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.25;
  margin: 0;
`

const StepHint = styled.p`
  color: #707887;
  font-size: 12px;
  line-height: 1.45;
  margin: 4px 0 0;
`

const SelectionButton = styled.button`
  align-items: center;
  background: #f7f8fb;
  border: 1px solid #e3e8f0;
  border-radius: 13px;
  color: #20242d;
  cursor: pointer;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 58px;
  padding: 12px 14px;
  text-align: left;
  transition: 0.16s ease all;
  width: 100%;

  &:hover {
    background: #f3f6fa;
    border-color: #d8deea;
  }
`

const SelectionMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const SelectionLabel = styled.span`
  color: #20242d;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SelectionSubtext = styled.span`
  color: #7d8492;
  font-size: 12.5px;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SimpleMethodIcon = styled.div<{ $logo?: string, $size?: number }>`
  background-image: ${({ $logo }) => $logo ? `url(${$logo})` : 'none'};
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  flex: 0 0 auto;
  height: ${({ $size }) => $size ?? 22}px;
  width: ${({ $size }) => $size ?? 22}px;
`

const NetworkOptionRow = styled.div`
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-width: 0;
  width: 100%;
`

const ArrivalMeta = styled.div`
  color: #596170;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.2;
  text-align: right;
  white-space: nowrap;
`

const DropdownSearchWrap = styled.div`
  padding: 10px;
`

const CompactDropdownSearch = styled(DropdownTextInput)`
  border: 1px solid #d8dde7;
  border-radius: 10px;
  box-sizing: border-box;
  min-height: 38px;
  padding: 0 10px;
`

const CashMethodGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    grid-template-columns: 1fr;
  }
`

const CashMethodCard = styled.button<{ $active?: boolean }>`
  align-items: flex-start;
  background: #fff;
  border: 1px solid ${({ $active }) => $active ? '#20242d' : '#e3e8f0'};
  border-radius: 12px;
  color: #20242d;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-height: 104px;
  padding: 12px;
  text-align: left;
  transition: 0.16s ease all;

  &:hover {
    background: #f8fafc;
    border-color: ${({ $active }) => $active ? '#20242d' : '#d8deea'};
  }
`

const CashMethodTitle = styled.span`
  color: #20242d;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.25;
`

const CashMethodDescription = styled.span`
  color: #737b8b;
  font-size: 12px;
  line-height: 1.4;
`

const CashMethodPillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: auto;
`

const SupportStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`

const FlowIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 620px;
`

const RailGrid = styled.div`
  background: #f4f6fa;
  border: 1px solid #e8edf5;
  border-radius: 13px;
  display: grid;
  gap: 3px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 3px;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    grid-template-columns: 1fr;
  }
`

const RailCard = styled.button<{ $active?: boolean }>`
  align-items: flex-start;
  background: ${({ $active }) => $active ? '#ffffff' : 'transparent'};
  border: 1px solid ${({ $active }) => $active ? '#e1e6ee' : 'transparent'};
  border-radius: 10px;
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(15, 23, 42, 0.07)' : 'none'};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-height: 58px;
  padding: 9px 11px;
  text-align: left;
  transition: 0.16s ease all;
`

const RailTitle = styled.span`
  color: #20242d;
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.25;
`

const RailText = styled.span`
  color: #737b8b;
  font-size: 12px;
  line-height: 1.4;
`

const PanelSection = styled.div`
  border-top: 1px solid #eef2f7;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 14px;
`

const RailHelper = styled.div`
  background: #fbfcfe;
  border: 1px solid #edf1f7;
  border-radius: 12px;
  color: #697386;
  font-size: 12.5px;
  line-height: 1.45;
  padding: 10px 12px;
`

const ToolbarRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
  min-width: 0;
`

const AccountTabs = styled.div`
  align-items: center;
  background: #f4f6fa;
  border: 1px solid #e8edf5;
  border-radius: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  padding: 3px;
`

const AccountTabButton = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => $active ? '#ffffff' : 'transparent'};
  border: 0;
  border-radius: 9px;
  box-shadow: ${({ $active }) => $active ? '0 1px 4px rgba(15, 23, 42, 0.08)' : 'none'};
  color: ${({ $active }) => $active ? '#20242d' : '#687183'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12.25px;
  font-weight: 500;
  min-height: 32px;
  padding: 7px 10px;
  white-space: nowrap;
`

const SearchBox = styled(TextInput)`
  flex: 1 1 auto;
  min-height: 38px;
  min-width: min(100%, 260px);
`

const AccountList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AccountCard = styled.button<{ $active?: boolean, $disabled?: boolean }>`
  align-items: center;
  background: ${({ $active }) => $active ? '#f7f9ff' : '#fff'};
  border: 1px solid ${({ $active }) => $active ? '#d8e2ff' : '#e8edf5'};
  border-radius: 13px;
  color: #20242d;
  cursor: ${({ $disabled }) => $disabled ? 'default' : 'pointer'};
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 58px;
  padding: 10px 12px;
  text-align: left;
`

const ManagedAccountList = styled.div`
  border: 1px solid #eef2f7;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  max-height: 430px;
  min-height: 0;
  overflow: auto;
`

const ManagedAccountRow = styled.button<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active }) => $active ? '#f7f9ff' : '#fff'};
  border: 0;
  border-bottom: 1px solid #eef2f7;
  color: #20242d;
  cursor: pointer;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(220px, 1.2fr) minmax(220px, 1.4fr) minmax(120px, 0.55fr) auto;
  min-height: 64px;
  padding: 11px 13px;
  text-align: left;

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }
`

const AccountName = styled.span`
  color: #20242d;
  font-size: 13.25px;
  font-weight: 500;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const AccountMeta = styled.span`
  color: #7d8492;
  font-size: 12px;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ManagedAccountMeta = styled(AccountMeta)`
  white-space: normal;
  overflow-wrap: anywhere;
`

const AccountActions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    justify-content: flex-start;
  }
`

const PaginationRow = styled.div`
  align-items: center;
  border-top: 1px solid #eef2f7;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  padding-top: 12px;
`

const DetailRow = styled.div`
  align-items: center;
  border-top: 1px solid #eef2f7;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-height: 42px;
  padding: 10px 0;
`

const DetailLabel = styled.span`
  color: #737b8b;
  font-size: 12px;
  line-height: 1.35;
`

const DetailValue = styled.span`
  color: #20242d;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  min-width: 0;
  overflow-wrap: anywhere;
  text-align: right;
`

const BalanceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const BalanceItem = styled.div`
  align-items: center;
  border: 1px solid #edf1f7;
  border-radius: 13px;
  display: grid;
  gap: 10px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 54px;
  padding: 9px 11px;
`

const ActivityList = styled.div`
  border: 1px solid #edf1f7;
  border-radius: 13px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`

const ActivityRow = styled.div`
  align-items: center;
  background: #fff;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1.25fr) minmax(96px, auto) minmax(96px, auto);
  min-height: 54px;
  padding: 10px 12px;

  & + & {
    border-top: 1px solid #edf1f7;
  }

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }
`

const ActivityTitle = styled.span`
  color: #20242d;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.25;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ActivityMeta = styled.span`
  color: #7d8492;
  font-size: 11.5px;
  line-height: 1.35;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Funding = observer((): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [testMode] = useTestMode()
  const viewParam = searchParams.get('view')
  const activeView: FundingView =
    viewParam === 'withdraw' || searchParams.get('flow') === 'withdraw'
      ? 'withdraw'
      : viewParam === 'deposit' || searchParams.get('flow') === 'deposit'
        ? 'deposit'
        : 'deposit'
  const flow: FundingFlow = activeView === 'withdraw' ? 'withdraw' : 'deposit'
  const initialDepositRail: FundingRail =
    searchParams.get('cash') === '1' || searchParams.get('rail') === 'bank' || searchParams.get('rail') === 'cash'
      ? 'bank'
      : 'crypto'
  const [depositRail, setDepositRail] = useState<FundingRail>(initialDepositRail)
  const [withdrawalRail, setWithdrawalRail] = useState<FundingRail>('bank')
  const [depositAccounts, setDepositAccounts] = useState<DepositAccount[]>([])
  const [withdrawalAccounts, setWithdrawalAccounts] = useState<WithdrawalAccount[]>([])
  const [selectedDepositAssetTicker, setSelectedDepositAssetTicker] = useState<string>()
  const [selectedDepositAccountId, setSelectedDepositAccountId] = useState<string>()
  const [selectedDepositMethodKey, setSelectedDepositMethodKey] = useState<string>()
  const [depositCashMethod, setDepositCashMethod] = useState<DepositCashMethod>('sepaMemo')
  const [depositAssetSearch, setDepositAssetSearch] = useState('')
  const [depositNetworkSearch, setDepositNetworkSearch] = useState('')
  const [depositAddressSearch, setDepositAddressSearch] = useState('')
  const [depositCashAccountSearch, setDepositCashAccountSearch] = useState('')
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)
  const [createDepositOpen, setCreateDepositOpen] = useState(false)
  const [createWithdrawalOpen, setCreateWithdrawalOpen] = useState(false)
  const [createWithdrawalType, setCreateWithdrawalType] = useState<AccountType>(AccountType.Fiat)
  const [withdrawalFormKey, setWithdrawalFormKey] = useState(0)
  const [selectedWithdrawalAccountId, setSelectedWithdrawalAccountId] = useState<string>()
  const [accountManagerTab, setAccountManagerTab] = useState<AccountManagerTab>('depositCrypto')
  const [accountSearch, setAccountSearch] = useState('')
  const [accountPage, setAccountPage] = useState(0)
  const [accountDetails, setAccountDetails] = useState<ManagedFundingAccount>()
  const [depositPickerOpen, setDepositPickerOpen] = useState(false)
  const [depositPickerSearch, setDepositPickerSearch] = useState('')
  const [virtualAccountCapabilities, setVirtualAccountCapabilities] = useState<BridgeVirtualAccountCapabilities>({
    canOpenAch: false,
    canOpenSepa: false,
    canOpenPix: false,
    canOpenSpei: false
  })
  const [recentDeposits, setRecentDeposits] = useState<Deposit[]>([])
  const [recentWithdrawals, setRecentWithdrawals] = useState<WithdrawalRequest[]>([])
  const [isLoadingActivity, setIsLoadingActivity] = useState(false)
  const [exportDepositsOpen, setExportDepositsOpen] = useState(false)
  const [exportWithdrawalsOpen, setExportWithdrawalsOpen] = useState(false)

  const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'

  const updateView = (nextView: FundingView): void => {
    setSearchParams({ view: nextView })
  }

  const updateFlow = (nextFlow: FundingFlow): void => {
    updateView(nextFlow)
  }

  const updateDepositRail = (nextRail: FundingRail): void => {
    setDepositRail(nextRail)
    if (activeView !== 'deposit') {
      return
    }

    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('view', 'deposit')
    nextParams.delete('flow')
    nextParams.delete('rail')
    if (nextRail === 'bank') {
      nextParams.set('cash', '1')
    } else {
      nextParams.delete('cash')
    }
    setSearchParams(nextParams)
  }

  const loadFundingData = async (): Promise<void> => {
    setIsLoadingAccounts(true)
    try {
      const [deposits, withdrawals, capabilities] = await Promise.all([
        Radom.listDepositAccounts(testMode, 0, 250, undefined, [DepositAccountType.Crypto, DepositAccountType.Fiat]),
        Radom.listWithdrawalAccounts(),
        Radom.getVirtualAccountsCapabilities().catch(() => ({
          canOpenAch: false,
          canOpenSepa: false,
          canOpenPix: false,
          canOpenSpei: false
        }))
      ])
      setDepositAccounts(deposits.data)
      setWithdrawalAccounts(withdrawals)
      setVirtualAccountCapabilities(capabilities)
    } catch (err) {
      console.error('Failed to load funding data', err)
      setDepositAccounts([])
      setWithdrawalAccounts([])
      setVirtualAccountCapabilities({
        canOpenAch: false,
        canOpenSepa: false,
        canOpenPix: false,
        canOpenSpei: false
      })
    } finally {
      setIsLoadingAccounts(false)
    }
  }

  const loadRecentActivity = async (): Promise<void> => {
    setIsLoadingActivity(true)
    try {
      const [deposits, withdrawals] = await Promise.all([
        Radom.listDeposits(testMode, [GeneralDepositMethod.Crypto, GeneralDepositMethod.Fiat], 0, 5),
        Radom.listWithdrawals(0, 5, testMode ? testNetworks : prodNetworks)
      ])
      setRecentDeposits(deposits.data)
      setRecentWithdrawals(withdrawals.data)
    } catch (err) {
      console.error('Failed to load funding activity', err)
      setRecentDeposits([])
      setRecentWithdrawals([])
    } finally {
      setIsLoadingActivity(false)
    }
  }

  useEffect(() => {
    Treasury.loadBalances()
    loadFundingData()
    loadRecentActivity()
  }, [testMode])

  useEffect(() => {
    if (activeView !== 'deposit') {
      return
    }

    if (searchParams.get('cash') === '1' || searchParams.get('rail') === 'bank' || searchParams.get('rail') === 'cash') {
      setDepositRail('bank')
    } else if (searchParams.get('cash') === '0' || searchParams.get('rail') === 'crypto') {
      setDepositRail('crypto')
    }
  }, [activeView, searchParams])

  useEffect(() => {
    setAccountPage(0)
  }, [accountManagerTab, accountSearch, testMode])

  const balanceRows = useMemo((): FundingBalanceRow[] => Treasury.managedBalances
    .filter(b => Number(b.balance) > 0)
    .map((b) => {
      const method = getMethod(b.network, b.token)
      const isFiat = b.network === 'Fiat' || !!method.isFiat
      const ticker = isFiat ? (b.token ?? method.ticker) : method.ticker
      const conversion = Treasury.priceQuotes
        .find(p => p.from.toLowerCase() === ticker.toLowerCase())
      const balance = Number(b.balance)
      const fiatValue = isFiat && ticker === defaultCurrency
        ? balance
        : conversion
          ? balance * conversion.value
          : 0

      return {
        source: b,
        ticker,
        displayName: isFiat ? `${ticker} balance` : method.currencyName ?? method.name,
        networkLabel: isFiat ? ticker : `${ticker} on ${method.hostChain?.name ?? method.name}`,
        fiatValue,
        isFiat
      }
    })
    .filter(row => {
      if (row.isFiat) {
        return !testMode
      }
      const method = getMethod(row.source.network, row.source.token)
      return testMode ? !!method.isTestnet : !method.isTestnet
    })
    .sort((a, b) => b.fiatValue - a.fiatValue), [
    Treasury.managedBalances,
    Treasury.priceQuotes,
    defaultCurrency,
    testMode
  ])

  const withdrawableAssets: WithdrawableAsset[] = balanceRows
    .filter(row => !row.isFiat)
    .map(row => ({
      network: row.source.network,
      token: row.source.token,
      maxAmount: row.source.balance,
      treasuryId: row.source.treasuryId
    }))

  const cryptoDepositOptions = useMemo((): CryptoDepositOption[] => {
    const supportedMethods = new Map<string, { network: string, token?: string }>()

    DepositMethods
      .filter(method => testMode ? !!method.isTestnet : !method.isTestnet)
      .flatMap(collectDepositMethodLeaves)
      .forEach(method => {
        supportedMethods.set(getDepositMethodKey(method.network, method.token), method)
      })

    return Array.from(supportedMethods.entries())
      .map(([key, method]) => ({
        key,
        network: method.network,
        token: method.token,
        accounts: depositAccounts.filter(account =>
          account.accountDetails.Crypto?.depositMethods.some(depositMethod =>
            getDepositMethodKey(depositMethod.network, depositMethod.token) === key)
        )
      }))
      .sort((a, b) => {
        const aMethod = getMethod(a.network, a.token)
        const bMethod = getMethod(b.network, b.token)
        const tickerDiff = sortedOrderIndex(POPULAR_DEPOSIT_ASSET_ORDER, aMethod.ticker) -
          sortedOrderIndex(POPULAR_DEPOSIT_ASSET_ORDER, bMethod.ticker)
        if (tickerDiff !== 0) {
          return tickerDiff
        }

        const networkDiff = sortedOrderIndex(POPULAR_DEPOSIT_NETWORK_ORDER, aMethod.hostChain?.name ?? aMethod.name) -
          sortedOrderIndex(POPULAR_DEPOSIT_NETWORK_ORDER, bMethod.hostChain?.name ?? bMethod.name)
        if (networkDiff !== 0) {
          return networkDiff
        }

        return `${aMethod.ticker} ${aMethod.hostChain?.name ?? aMethod.name}`
          .localeCompare(`${bMethod.ticker} ${bMethod.hostChain?.name ?? bMethod.name}`)
      })
  }, [depositAccounts, testMode])

  const cryptoDepositAssetOptions = useMemo((): CryptoDepositAssetOption[] => {
    const assets = new Map<string, CryptoDepositAssetOption>()

    cryptoDepositOptions.forEach(option => {
      const method = getMethod(option.network, option.token)
      const existing = assets.get(method.ticker)
      if (existing) {
        existing.options.push(option)
        return
      }

      assets.set(method.ticker, {
        ticker: method.ticker,
        name: method.currencyName ?? method.name,
        options: [option]
      })
    })

    return Array.from(assets.values())
      .map(asset => ({
        ...asset,
        options: asset.options.sort((a, b) => {
          const aMethod = getMethod(a.network, a.token)
          const bMethod = getMethod(b.network, b.token)
          const networkDiff = sortedOrderIndex(POPULAR_DEPOSIT_NETWORK_ORDER, aMethod.hostChain?.name ?? aMethod.name) -
            sortedOrderIndex(POPULAR_DEPOSIT_NETWORK_ORDER, bMethod.hostChain?.name ?? bMethod.name)
          if (networkDiff !== 0) {
            return networkDiff
          }

          return (aMethod.hostChain?.name ?? aMethod.name).localeCompare(bMethod.hostChain?.name ?? bMethod.name)
        })
      }))
      .sort((a, b) => {
        const orderDiff = sortedOrderIndex(POPULAR_DEPOSIT_ASSET_ORDER, a.ticker) -
          sortedOrderIndex(POPULAR_DEPOSIT_ASSET_ORDER, b.ticker)
        if (orderDiff !== 0) {
          return orderDiff
        }

        return a.ticker.localeCompare(b.ticker)
      })
  }, [cryptoDepositOptions])

  const selectedCryptoDepositAsset = cryptoDepositAssetOptions.find(asset => asset.ticker === selectedDepositAssetTicker) ??
    cryptoDepositAssetOptions[0]
  const selectedCryptoDepositNetworkOptions = selectedCryptoDepositAsset?.options ?? []
  const selectedCryptoDepositOption = selectedCryptoDepositNetworkOptions.find(option => option.key === selectedDepositMethodKey) ??
    selectedCryptoDepositNetworkOptions[0]

  const allFiatDepositAccounts = depositAccounts.filter(account => !!account.accountDetails.Fiat)
  const isSepaDepositAccount = (account: DepositAccount): boolean => {
    const fiat = account.accountDetails.Fiat
    const iban = fiat?.accountType.iban
    const searchable = [
      account.name,
      fiat?.bankName,
      iban?.sourcePaymentRails?.join(' '),
      iban?.destinationPaymentRail
    ].join(' ').toLowerCase()

    return searchable.includes('sepa') || searchable.includes('ivy')
  }
  const virtualDepositAccounts = allFiatDepositAccounts.filter(account => !isSepaDepositAccount(account))
  const virtualAccountsEnabled = Object.values(virtualAccountCapabilities).some(Boolean) || virtualDepositAccounts.length > 0
  const cashDepositAccounts = depositCashMethod === 'openBanking'
    ? []
    : depositCashMethod === 'sepaMemo'
      ? allFiatDepositAccounts.filter(isSepaDepositAccount)
      : virtualDepositAccounts

  const filteredCryptoDepositAssetOptions = cryptoDepositAssetOptions.filter(asset => {
    const query = depositAssetSearch.trim().toLowerCase()
    if (!query) {
      return true
    }

    const methods = asset.options.map(option => getMethod(option.network, option.token))
    const terms = [
      asset.ticker,
      asset.name,
      ...methods.flatMap(method => [
        method.name,
        method.currencyName
      ])
    ]

    return terms.some(term => term?.toLowerCase().includes(query))
  })

  const filteredCryptoDepositNetworkOptions = selectedCryptoDepositNetworkOptions.filter(option => {
    const query = depositNetworkSearch.trim().toLowerCase()
    if (!query) {
      return true
    }

    const method = getMethod(option.network, option.token)
    const terms = [
      method.hostChain?.name ?? method.name,
      method.ticker,
      option.network,
      option.token
    ]

    return terms.some(term => term?.toLowerCase().includes(query))
  })

  useEffect(() => {
    if (depositRail !== 'crypto') {
      return
    }

    if (cryptoDepositAssetOptions.length === 0) {
      setSelectedDepositAssetTicker(undefined)
      setSelectedDepositMethodKey(undefined)
      return
    }

    if (selectedDepositAssetTicker && !cryptoDepositAssetOptions.some(asset => asset.ticker === selectedDepositAssetTicker)) {
      setSelectedDepositAssetTicker(undefined)
      setSelectedDepositMethodKey(undefined)
      setSelectedDepositAccountId(undefined)
      return
    }

    if (selectedDepositMethodKey && !selectedCryptoDepositNetworkOptions.some(option => option.key === selectedDepositMethodKey)) {
      setSelectedDepositMethodKey(undefined)
      setSelectedDepositAccountId(undefined)
    }
  }, [
    depositRail,
    cryptoDepositAssetOptions,
    selectedCryptoDepositNetworkOptions,
    selectedDepositAssetTicker,
    selectedDepositMethodKey
  ])

  useEffect(() => {
    setSelectedDepositAccountId(undefined)
    setDepositAddressSearch('')
    setDepositCashAccountSearch('')
  }, [depositRail, depositCashMethod])

  useEffect(() => {
    setSelectedDepositAccountId(undefined)
    setDepositNetworkSearch('')
    setDepositAddressSearch('')
  }, [selectedDepositAssetTicker, selectedDepositMethodKey])

  const visibleDepositAccounts = depositAccounts.filter(account =>
    depositRail === 'crypto'
      ? !!account.accountDetails.Crypto &&
        (!selectedCryptoDepositOption ||
          account.accountDetails.Crypto.depositMethods.some(method =>
            getDepositMethodKey(method.network, method.token) === selectedCryptoDepositOption.key))
      : cashDepositAccounts.some(cashAccount => cashAccount.id === account.id)
  )
  const selectedDepositAccount = visibleDepositAccounts.find(account => account.id === selectedDepositAccountId) ??
    visibleDepositAccounts[0]
  const searchedVisibleDepositAccounts = visibleDepositAccounts.filter(account => {
    const query = (depositRail === 'crypto' ? depositAddressSearch : depositCashAccountSearch).trim().toLowerCase()
    if (!query) {
      return true
    }

    const fiat = account.accountDetails.Fiat
    const crypto = account.accountDetails.Crypto
    const terms = [
      account.id,
      account.name,
      getDepositAccountMeta(account),
      account.customer?.name,
      account.customer?.email,
      account.customer?.id,
      crypto?.address,
      fiat?.bankName,
      fiat?.accountType.iban?.iban,
      fiat?.accountType.iban?.bic,
      fiat?.accountType.us?.accountNumber,
      fiat?.accountType.us?.routingNumber
    ]

    return terms.some(term => term?.toLowerCase().includes(query))
  })
  const visibleWithdrawalAccounts = withdrawalAccounts.filter(account =>
    withdrawalRail === 'crypto' ? !!account.accountDetails.Crypto : !!account.accountDetails.Fiat
  )
  const selectedWithdrawalAccount = visibleWithdrawalAccounts.find(account => account.id === selectedWithdrawalAccountId)
  const isBridgeEurOfframp = User.organization?.legacyEurOfframpEnabled === true

  const openWithdrawalAccountModal = (type: AccountType): void => {
    setCreateWithdrawalType(type)
    setCreateWithdrawalOpen(true)
  }

  const openAccountWorkspace = (tab: AccountManagerTab): void => {
    setAccountManagerTab(tab)
    setAccountSearch('')
    updateView('accounts')
  }

  const selectManagedAccount = (managedAccount: ManagedFundingAccount): void => {
    if (managedAccount.kind === 'deposit') {
      setDepositRail(managedAccount.rail)
      setSelectedDepositAccountId(managedAccount.account.id)
      updateFlow('deposit')
      return
    }

    setWithdrawalRail(managedAccount.rail)
    setSelectedWithdrawalAccountId(managedAccount.account.id)
    setWithdrawalFormKey(v => v + 1)
    updateFlow('withdraw')
  }

  const accountManagerAccounts = useMemo((): ManagedFundingAccount[] => {
    const depositCrypto = depositAccounts
      .filter(account => !!account.accountDetails.Crypto)
      .map(account => ({ kind: 'deposit' as const, rail: 'crypto' as const, account }))
    const depositBank = depositAccounts
      .filter(account => !!account.accountDetails.Fiat)
      .map(account => ({ kind: 'deposit' as const, rail: 'bank' as const, account }))
    const withdrawCrypto = withdrawalAccounts
      .filter(account => !!account.accountDetails.Crypto)
      .map(account => ({ kind: 'withdraw' as const, rail: 'crypto' as const, account }))
    const withdrawBank = withdrawalAccounts
      .filter(account => !!account.accountDetails.Fiat)
      .map(account => ({ kind: 'withdraw' as const, rail: 'bank' as const, account }))

    if (accountManagerTab === 'depositCrypto') return depositCrypto
    if (accountManagerTab === 'depositBank') return depositBank
    if (accountManagerTab === 'withdrawCrypto') return withdrawCrypto
    return withdrawBank
  }, [accountManagerTab, depositAccounts, withdrawalAccounts])

  const getManagedAccountName = (managedAccount: ManagedFundingAccount): string =>
    managedAccount.account.name?.trim() || (managedAccount.kind === 'deposit' ? 'Deposit account' : 'Withdrawal account')

  const getManagedAccountMeta = (managedAccount: ManagedFundingAccount): string =>
    managedAccount.kind === 'deposit'
      ? getDepositAccountMeta(managedAccount.account)
      : getWithdrawalAccountMeta(managedAccount.account)

  const getManagedAccountOwner = (managedAccount: ManagedFundingAccount): string => {
    if (managedAccount.kind === 'deposit') {
      const customer = managedAccount.account.customer
      if (customer) {
        return customer.name || customer.email || 'Customer'
      }

      return managedAccount.account.accountDetails.Crypto?.isExternal ? 'External' : 'Own account'
    }

    const fiat = managedAccount.account.accountDetails.Fiat
    if (fiat) {
      return fiat.accountOwnerName || fiat.accountOwnerType || 'Bank account'
    }

    return managedAccount.account.accountDetails.Crypto?.network ?? 'Crypto wallet'
  }

  const formatCreatedAt = (createdAt?: string): string => {
    if (!createdAt) return '-'
    const date = new Date(createdAt)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const filteredAccountManagerAccounts = accountManagerAccounts.filter(managedAccount => {
    const query = accountSearch.trim().toLowerCase()
    if (!query) return true

    const terms = [
      managedAccount.account.id,
      getManagedAccountName(managedAccount),
      getManagedAccountMeta(managedAccount),
      getManagedAccountOwner(managedAccount),
      managedAccount.kind,
      managedAccount.rail,
      managedAccount.account.status
    ]

    return terms.some(term => term?.toLowerCase().includes(query))
  })
  const managerPageSize = 10
  const pagedAccountManagerAccounts = filteredAccountManagerAccounts
    .slice(accountPage * managerPageSize, (accountPage + 1) * managerPageSize)
  const managerPageCount = Math.max(1, Math.ceil(filteredAccountManagerAccounts.length / managerPageSize))

  const renderRailSelector = (): ReactElement => {
    const activeRail = flow === 'deposit' ? depositRail : withdrawalRail
    const bankLabel = 'Cash'

    return <UnderlineTabs role='tablist' aria-label={`${flow} method`}>
      {[
        { key: 'crypto' as FundingRail, label: 'Crypto' },
        { key: 'bank' as FundingRail, label: bankLabel }
      ].map(option => <UnderlineTab
        key={option.key}
        type='button'
        role='tab'
        aria-selected={activeRail === option.key}
        $active={activeRail === option.key}
        onClick={() => {
          if (flow === 'deposit') {
            updateDepositRail(option.key)
          } else {
            setWithdrawalRail(option.key)
          }
        }}>
        {option.label}
      </UnderlineTab>)}
    </UnderlineTabs>
  }

  const renderDepositAccountDetails = (): ReactElement => {
    if (isLoadingAccounts) {
      return <LoadingBar style={{ borderRadius: 20, height: 160, width: '100%' }} />
    }

    if (!selectedDepositAccount) {
      return <EmptyState style={{ minHeight: 180 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
          <span>{depositRail === 'crypto' ? 'No crypto deposit addresses yet.' : 'No bank deposit accounts yet.'}</span>
          {depositRail === 'crypto' && <PrimaryButton onClick={() => setCreateDepositOpen(true)}>
            <ButtonLeadingIcon>+</ButtonLeadingIcon>
            <span>Create address</span>
          </PrimaryButton>}
        </div>
      </EmptyState>
    }

    if (selectedDepositAccount.accountDetails.Fiat) {
      const fiat = selectedDepositAccount.accountDetails.Fiat
      const iban = fiat.accountType.iban

      return <div style={{ display: 'flex', flexDirection: 'column' }}>
        <DetailRow style={{ borderTop: 0 }}>
          <DetailLabel>Name</DetailLabel>
          <DetailValue>{selectedDepositAccount.name}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Bank</DetailLabel>
          <DetailValue>{fiat.bankName ?? 'Bank account'}</DetailValue>
        </DetailRow>
        {iban && <DetailRow>
          <DetailLabel>Account holder</DetailLabel>
          <DetailValue>{iban.accountHolderName ?? '-'}</DetailValue>
        </DetailRow>}
        {iban && <DetailRow>
          <DetailLabel>IBAN</DetailLabel>
          <div style={{ alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
            <DetailValue style={{ fontFamily: 'var(--font-mono)' }}>{iban.iban}</DetailValue>
            <CopyButton text={iban.iban} />
          </div>
        </DetailRow>}
        {iban && <DetailRow>
          <DetailLabel>BIC</DetailLabel>
          <div style={{ alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
            <DetailValue style={{ fontFamily: 'var(--font-mono)' }}>{iban.bic}</DetailValue>
            <CopyButton text={iban.bic} />
          </div>
        </DetailRow>}
        <DetailRow>
          <DetailLabel>Rail</DetailLabel>
          <DetailValue>{iban?.sourcePaymentRails?.join(', ') ?? 'SEPA'}</DetailValue>
        </DetailRow>
      </div>
    }

    const address = selectedDepositAccount.accountDetails.Crypto?.address ?? ''
    const supportedMethods = selectedDepositAccount.accountDetails.Crypto?.depositMethods ?? []
    const selectedDepositMethod = selectedCryptoDepositOption
      ? getMethod(selectedCryptoDepositOption.network, selectedCryptoDepositOption.token)
      : undefined

    return <div style={{ display: 'flex', flexDirection: 'column' }}>
      <DetailRow style={{ borderTop: 0 }}>
        <DetailLabel>Name</DetailLabel>
        <DetailValue>{selectedDepositAccount.name}</DetailValue>
      </DetailRow>
      <DetailRow>
        <DetailLabel>Address</DetailLabel>
        <div style={{ alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
          <DetailValue style={{ fontFamily: 'var(--font-mono)' }}>{shortAddress(address, 8)}</DetailValue>
          <CopyButton text={address} />
        </div>
      </DetailRow>
      <DetailRow>
        <DetailLabel>{selectedDepositMethod ? 'Selected asset' : 'Supported assets'}</DetailLabel>
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
          {selectedDepositMethod && selectedCryptoDepositOption
            ? <InlinePill>
              {selectedDepositMethod.ticker} {selectedDepositMethod.hostChain?.name ?? selectedDepositMethod.name}
            </InlinePill>
            : <>
              {supportedMethods.slice(0, 5).map((method) => {
                const managedMethod = getMethod(method.network, method.token)
                return <InlinePill key={`${method.network}-${method.token ?? 'native'}`}>
                  {managedMethod.ticker} {managedMethod.hostChain?.name ?? managedMethod.name}
                </InlinePill>
              })}
              {supportedMethods.length > 5 && <InlinePill>+{supportedMethods.length - 5}</InlinePill>}
            </>}
        </div>
      </DetailRow>
      <DetailRow>
        <DetailLabel>Account ID</DetailLabel>
        <div style={{ alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
          <DetailValue style={{ fontFamily: 'var(--font-mono)' }}>{shortAddress(selectedDepositAccount.id, 8)}</DetailValue>
          <CopyButton text={selectedDepositAccount.id} />
        </div>
      </DetailRow>
    </div>
  }

  const renderDepositPanel = (): ReactElement => (
    <ActionGrid>
      {renderRailSelector()}
      <PanelSection>
        <SurfaceHeader>
          <div>
            <SurfaceTitle>{depositRail === 'crypto' ? 'Deposit address' : 'Bank deposit account'}</SurfaceTitle>
            <SurfaceSubtext>{depositRail === 'crypto' ? 'Select an address or create a new one.' : 'View saved bank funding details.'}</SurfaceSubtext>
          </div>
          <PageActionRow>
            <SecondaryButton
              onClick={() => {
                setDepositPickerSearch('')
                setDepositPickerOpen(true)
              }}
              style={{ minHeight: 36 }}>
              Change
            </SecondaryButton>
            {depositRail === 'crypto' && <PrimaryButton
              onClick={() => setCreateDepositOpen(true)}
              style={{ minHeight: 36 }}>
              <ButtonLeadingIcon>+</ButtonLeadingIcon>
              <span>Create address</span>
            </PrimaryButton>}
          </PageActionRow>
        </SurfaceHeader>
        {renderDepositAccountDetails()}
      </PanelSection>
    </ActionGrid>
  )

  function getDepositAccountMeta(account: DepositAccount): string {
    if (account.accountDetails.Crypto) {
      return shortAddress(account.accountDetails.Crypto.address, 8)
    }

    const fiat = account.accountDetails.Fiat
    const iban = fiat?.accountType.iban
    if (iban) {
      return `${fiat?.bankName ?? 'Bank'} - ${iban.iban}`
    }

    return fiat?.bankName ?? 'Bank deposit account'
  }

  function getWithdrawalAccountMeta(account: WithdrawalAccount): string {
    if (account.accountDetails.Crypto) {
      return shortAddress(account.accountDetails.Crypto.address, 8)
    }

    const fiat = account.accountDetails.Fiat
    const iban = fiat?.accountType.iban
    const owner = fiat?.accountOwnerName ?? 'Bank account'
    return iban ? `${owner} - ${iban.iban}` : owner
  }

  const renderAccountSideList = (): ReactElement => {
    const accounts = flow === 'deposit' ? visibleDepositAccounts : visibleWithdrawalAccounts
    const title = flow === 'deposit'
      ? depositRail === 'crypto' ? 'Deposit addresses' : 'Bank deposit accounts'
      : withdrawalRail === 'crypto' ? 'Crypto destinations' : 'Bank destinations'
    const emptyText = flow === 'deposit'
      ? depositRail === 'crypto' ? 'No deposit addresses yet.' : 'No bank deposit accounts yet.'
      : withdrawalRail === 'crypto' ? 'No crypto withdrawal accounts yet.' : 'No bank withdrawal accounts yet.'

    return <Surface>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>{title}</SurfaceTitle>
          <SurfaceSubtext>{accounts.length} saved · quick select</SurfaceSubtext>
        </div>
        <PageActionRow>
          <SecondaryButton
            onClick={() => {
              openAccountWorkspace(flow === 'deposit'
                ? depositRail === 'crypto' ? 'depositCrypto' : 'depositBank'
                : withdrawalRail === 'crypto' ? 'withdrawCrypto' : 'withdrawBank')
            }}
            style={{ minHeight: 34 }}>
            View all
          </SecondaryButton>
          {flow === 'deposit' && depositRail === 'crypto' && <SecondaryButton onClick={() => setCreateDepositOpen(true)} style={{ minHeight: 34 }}>
            New
          </SecondaryButton>}
          {flow === 'withdraw' && <SecondaryButton
            onClick={() => openWithdrawalAccountModal(withdrawalRail === 'crypto' ? AccountType.Crypto : AccountType.Fiat)}
            style={{ minHeight: 34 }}>
            New
          </SecondaryButton>}
        </PageActionRow>
      </SurfaceHeader>

      {isLoadingAccounts
        ? <LoadingBar style={{ borderRadius: 20, height: 120, width: '100%' }} />
        : accounts.length === 0
          ? <EmptyState>{emptyText}</EmptyState>
          : <AccountList>
            {accounts.slice(0, 5).map(account => {
              if ('accountDetails' in account && flow === 'deposit') {
                const depositAccount = account as DepositAccount
                const active = depositAccount.id === selectedDepositAccount?.id
                return <AccountCard
                  key={depositAccount.id}
                  type='button'
                  $active={active}
                  onClick={() => setSelectedDepositAccountId(depositAccount.id)}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <AccountName>{depositAccount.name}</AccountName>
                    <AccountMeta>{getDepositAccountMeta(depositAccount)}</AccountMeta>
                  </div>
                  <InlinePill>{formatDepositAccountType(depositAccount.accountDetails)}</InlinePill>
                </AccountCard>
              }

              const withdrawalAccount = account as WithdrawalAccount
              const isIndividualIban = withdrawalAccount.accountDetails.Fiat?.accountOwnerType?.toLowerCase() === 'individual' &&
                !!withdrawalAccount.accountDetails.Fiat?.accountType.iban
              const isPayoutOnlyIban = isIndividualIban && !isBridgeEurOfframp
              return <AccountCard
                key={withdrawalAccount.id}
                as={isPayoutOnlyIban ? 'div' : undefined}
                type={isPayoutOnlyIban ? undefined : 'button'}
                $active={selectedWithdrawalAccount?.id === withdrawalAccount.id}
                $disabled={isPayoutOnlyIban}
                onClick={() => {
                  if (!isPayoutOnlyIban) {
                    setSelectedWithdrawalAccountId(withdrawalAccount.id)
                    setWithdrawalFormKey(v => v + 1)
                  }
                }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                  <AccountName>{withdrawalAccount.name}</AccountName>
                  <AccountMeta>{getWithdrawalAccountMeta(withdrawalAccount)}</AccountMeta>
                </div>
                <InlinePill $tone={isPayoutOnlyIban ? 'amber' : 'neutral'}>
                  {isPayoutOnlyIban ? 'Payout only' : withdrawalAccount.accountDetails.Fiat ? 'Bank' : 'Crypto'}
                </InlinePill>
              </AccountCard>
            })}
            {accounts.length > 5 && <RailHelper>{accounts.length - 5} more saved. Use View all to search, select, or manage them.</RailHelper>}
          </AccountList>}
    </Surface>
  }

  const renderBalances = (): ReactElement => (
    <Surface>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>Available funds</SurfaceTitle>
          <SurfaceSubtext>{testMode ? 'Test mode balances' : 'Live balances'}</SurfaceSubtext>
        </div>
        <InlinePill $tone={testMode ? 'amber' : 'green'}>{testMode ? 'Test mode' : 'Live mode'}</InlinePill>
      </SurfaceHeader>
      {Treasury.loading
        ? <LoadingBar style={{ borderRadius: 20, height: 120, width: '100%' }} />
        : balanceRows.length === 0
          ? <EmptyState>No settled funds yet.</EmptyState>
          : <BalanceList>
            {balanceRows.slice(0, 8).map(row => {
              const method = getMethod(row.source.network, row.source.token)
              return <BalanceItem key={`${row.source.network}-${row.source.token ?? 'native'}-${row.source.treasuryId ?? 'radom'}`}>
                <PaymentMethodDisplay
                  method={{ network: row.source.network, token: row.source.token }}
                  excludeName
                  size={24} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <AccountName>{row.displayName}</AccountName>
                  <AccountMeta>{row.networkLabel}</AccountMeta>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end', textAlign: 'right' }}>
                  <span style={{ color: '#20242d', fontSize: 12.75, fontWeight: 500 }}>
                    {formatAssetAmount(row.source.balance, row.ticker)} {row.ticker}
                  </span>
                  <span style={{ color: '#7d8492', fontSize: 11.5 }}>
                    {row.fiatValue ? formatCurrency(row.fiatValue, defaultCurrency) : method.isFiat ? row.ticker : '-'}
                  </span>
                </div>
              </BalanceItem>
            })}
          </BalanceList>}
    </Surface>
  )

  const formatActivityDate = (value?: string): string => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const renderDepositAmount = (deposit: Deposit): ReactElement => {
    if (deposit.depositMethod.Crypto) {
      const method = getMethod(deposit.depositMethod.Crypto.network, deposit.depositMethod.Crypto.token)
      return <span>{formatAssetAmount(deposit.netAmount, method.ticker)} {method.ticker}</span>
    }

    if (deposit.depositMethod.Fiat) {
      const method = getMethod(deposit.depositMethod.Fiat.onrampNetwork, deposit.depositMethod.Fiat.onrampToken)
      return <span>{formatAssetAmount(deposit.netAmount, method.ticker)} {method.ticker}</span>
    }

    return <span>-</span>
  }

  const renderDepositActivity = (): ReactElement => (
    <Surface>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>Recent deposits</SurfaceTitle>
          <SurfaceSubtext>Latest crypto and fiat funding events.</SurfaceSubtext>
        </div>
        <SecondaryButton onClick={() => setExportDepositsOpen(true)} style={{ minHeight: 34, whiteSpace: 'nowrap' }}>
          Export CSV
        </SecondaryButton>
      </SurfaceHeader>
      {isLoadingActivity
        ? <LoadingBar style={{ borderRadius: 20, height: 120, width: '100%' }} />
        : recentDeposits.length === 0
          ? <EmptyState style={{ minHeight: 96 }}>No deposits yet.</EmptyState>
          : <ActivityList>
            {recentDeposits.map(deposit => {
              const crypto = deposit.depositMethod.Crypto
              const fiat = deposit.depositMethod.Fiat
              const method = crypto
                ? getMethod(crypto.network, crypto.token)
                : fiat
                  ? getMethod(fiat.onrampNetwork, fiat.onrampToken)
                  : undefined

              return <ActivityRow key={deposit.id}>
                <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0 }}>
                  {method && <PaymentMethodDisplay
                    method={crypto ? { network: crypto.network, token: crypto.token } : { network: fiat?.onrampNetwork ?? 'Fiat', token: fiat?.onrampToken }}
                    excludeName
                    size={22} />}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <ActivityTitle>{deposit.depositAccountName || 'Deposit'}</ActivityTitle>
                    <ActivityMeta>{crypto ? `${method?.ticker} on ${method?.hostChain?.name ?? method?.name}` : `${fiat?.fiatCurrency ?? 'Fiat'} bank deposit`}</ActivityMeta>
                  </div>
                </div>
                <div style={{ color: '#20242d', fontSize: 12.75, fontWeight: 500, textAlign: 'right' }}>
                  {renderDepositAmount(deposit)}
                </div>
                <ActivityMeta style={{ textAlign: 'right' }}>{formatActivityDate(deposit.depositedAt)}</ActivityMeta>
              </ActivityRow>
            })}
          </ActivityList>}
    </Surface>
  )

  const renderWithdrawalActivity = (): ReactElement => (
    <Surface>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>Recent withdrawals</SurfaceTitle>
          <SurfaceSubtext>Latest crypto and bank withdrawal requests.</SurfaceSubtext>
        </div>
        <SecondaryButton onClick={() => setExportWithdrawalsOpen(true)} style={{ minHeight: 34, whiteSpace: 'nowrap' }}>
          Export CSV
        </SecondaryButton>
      </SurfaceHeader>
      {isLoadingActivity
        ? <LoadingBar style={{ borderRadius: 20, height: 120, width: '100%' }} />
        : recentWithdrawals.length === 0
          ? <EmptyState style={{ minHeight: 96 }}>No withdrawals yet.</EmptyState>
          : <ActivityList>
            {recentWithdrawals.map(withdrawal => {
              const method = getMethod(withdrawal.network, withdrawal.token)
              const statusTone = withdrawal.withdrawalStatus === 'complete'
                ? 'green'
                : withdrawal.withdrawalStatus === 'failed'
                  ? 'red'
                  : withdrawal.withdrawalStatus === 'processing' ? 'blue' : 'amber'

              return <ActivityRow key={withdrawal.id}>
                <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0 }}>
                  <PaymentMethodDisplay
                    method={{ network: withdrawal.network, token: withdrawal.token }}
                    excludeName
                    size={22} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <ActivityTitle>{method.currencyName ?? method.name}</ActivityTitle>
                    <ActivityMeta>{withdrawal.withdrawalAccountName ?? shortAddress(withdrawal.withdrawalAccountId, 6)}</ActivityMeta>
                  </div>
                </div>
                <div style={{ color: '#20242d', fontSize: 12.75, fontWeight: 500, textAlign: 'right' }}>
                  {formatAssetAmount(withdrawal.withdrawalAmount, method.ticker)} {method.ticker}
                </div>
                <div style={{ alignItems: 'flex-end', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <InlinePill $tone={statusTone}>{withdrawal.withdrawalStatus}</InlinePill>
                  <ActivityMeta>{formatActivityDate(withdrawal.requestedAt)}</ActivityMeta>
                </div>
              </ActivityRow>
            })}
          </ActivityList>}
    </Surface>
  )

  const renderAccountManager = (): ReactElement => {
    const tabOptions: Array<{ key: AccountManagerTab, label: string, count: number }> = [
      { key: 'depositCrypto', label: 'Deposit addresses', count: depositAccounts.filter(a => !!a.accountDetails.Crypto).length },
      { key: 'depositBank', label: 'Bank deposits', count: depositAccounts.filter(a => !!a.accountDetails.Fiat).length },
      { key: 'withdrawCrypto', label: 'Withdrawal wallets', count: withdrawalAccounts.filter(a => !!a.accountDetails.Crypto).length },
      { key: 'withdrawBank', label: 'Bank withdrawals', count: withdrawalAccounts.filter(a => !!a.accountDetails.Fiat).length }
    ]
    const isDepositTab = accountManagerTab === 'depositCrypto' || accountManagerTab === 'depositBank'
    const isCryptoTab = accountManagerTab === 'depositCrypto' || accountManagerTab === 'withdrawCrypto'
    const emptyCopy = isDepositTab
      ? isCryptoTab ? 'No deposit addresses yet.' : 'No bank deposit accounts yet.'
      : isCryptoTab ? 'No withdrawal wallets yet.' : 'No bank withdrawal accounts yet.'

    return <Surface id='funding-account-manager' style={{ gap: 14 }}>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>Saved accounts</SurfaceTitle>
          <SurfaceSubtext>Search and manage deposit details, withdrawal wallets, and bank accounts from one place.</SurfaceSubtext>
        </div>
        <PageActionRow>
          {accountManagerTab === 'depositCrypto' && <PrimaryButton onClick={() => setCreateDepositOpen(true)} style={{ minHeight: 36, whiteSpace: 'nowrap' }}>
            <ButtonLeadingIcon>+</ButtonLeadingIcon>
            <span>New address</span>
          </PrimaryButton>}
          {(accountManagerTab === 'withdrawCrypto' || accountManagerTab === 'withdrawBank') && <PrimaryButton
            onClick={() => openWithdrawalAccountModal(accountManagerTab === 'withdrawCrypto' ? AccountType.Crypto : AccountType.Fiat)}
            style={{ minHeight: 36, whiteSpace: 'nowrap' }}>
            <ButtonLeadingIcon>+</ButtonLeadingIcon>
            <span>Add destination</span>
          </PrimaryButton>}
        </PageActionRow>
      </SurfaceHeader>

      <ToolbarRow>
        <AccountTabs role='tablist' aria-label='Funding account type'>
          {tabOptions.map(option => <AccountTabButton
            key={option.key}
            type='button'
            role='tab'
            aria-selected={accountManagerTab === option.key}
            $active={accountManagerTab === option.key}
            onClick={() => setAccountManagerTab(option.key)}>
            {option.label} ({option.count})
          </AccountTabButton>)}
        </AccountTabs>
        <SearchBox
          value={accountSearch}
          onChange={e => setAccountSearch(e.target.value)}
          placeholder='Search name, address, IBAN, customer, or ID'
        />
      </ToolbarRow>

      {isLoadingAccounts
        ? <LoadingBar style={{ borderRadius: 20, height: 160, width: '100%' }} />
        : filteredAccountManagerAccounts.length === 0
          ? <EmptyState>{accountSearch.trim() ? 'No accounts match this search.' : emptyCopy}</EmptyState>
          : <>
            <ManagedAccountList>
              {pagedAccountManagerAccounts.map(managedAccount => {
                const account = managedAccount.account
                const active = managedAccount.kind === 'deposit'
                  ? account.id === selectedDepositAccount?.id && managedAccount.rail === depositRail
                  : account.id === selectedWithdrawalAccountId && managedAccount.rail === withdrawalRail
                const isIndividualIban = managedAccount.kind === 'withdraw' &&
                  managedAccount.account.accountDetails.Fiat?.accountOwnerType?.toLowerCase() === 'individual' &&
                  !!managedAccount.account.accountDetails.Fiat?.accountType.iban
                const isPayoutOnlyIban = isIndividualIban && !isBridgeEurOfframp
                const primaryAction = managedAccount.kind === 'deposit'
                  ? managedAccount.rail === 'crypto' ? 'Use address' : 'View details'
                  : managedAccount.rail === 'bank' && isPayoutOnlyIban ? 'Payout only' : 'Use destination'

                return <ManagedAccountRow
                  key={`${managedAccount.kind}-${account.id}`}
                  type='button'
                  $active={active}
                  onClick={() => {
                    if (isPayoutOnlyIban) {
                      setAccountDetails(managedAccount)
                    } else {
                      selectManagedAccount(managedAccount)
                    }
                  }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                    <AccountName>{getManagedAccountName(managedAccount)}</AccountName>
                    <div style={{ alignItems: 'center', display: 'flex', gap: 6, minWidth: 0 }}>
                      <ManagedAccountMeta style={{ fontFamily: 'var(--font-mono)' }}>{shortAddress(account.id, 6)}</ManagedAccountMeta>
                      <CopyButton text={account.id} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                    <ManagedAccountMeta>{getManagedAccountMeta(managedAccount)}</ManagedAccountMeta>
                    <ManagedAccountMeta>{getManagedAccountOwner(managedAccount)}</ManagedAccountMeta>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                    <ManagedAccountMeta>{formatCreatedAt(account.createdAt)}</ManagedAccountMeta>
                    <InlinePill $tone={account.status === 'active' || account.status === 'verfied' ? 'green' : 'amber'}>
                      {account.status === 'verfied' ? 'verified' : account.status}
                    </InlinePill>
                  </div>
                  <AccountActions>
                    <SecondaryButton
                      disabled={managedAccount.kind === 'withdraw' && managedAccount.rail === 'bank' && isPayoutOnlyIban}
                      onClick={e => {
                        e.stopPropagation()
                        if (!(managedAccount.kind === 'withdraw' && managedAccount.rail === 'bank' && isPayoutOnlyIban)) {
                          selectManagedAccount(managedAccount)
                        }
                      }}
                      style={{ minHeight: 34, padding: '0 12px' }}>
                      {primaryAction}
                    </SecondaryButton>
                    <SecondaryButton
                      onClick={e => {
                        e.stopPropagation()
                        setAccountDetails(managedAccount)
                      }}
                      style={{ minHeight: 34, padding: '0 12px' }}>
                      Details
                    </SecondaryButton>
                  </AccountActions>
                </ManagedAccountRow>
              })}
            </ManagedAccountList>
            <PaginationRow>
              <SurfaceSubtext style={{ margin: 0 }}>
                Showing {accountPage * managerPageSize + 1}-{Math.min((accountPage + 1) * managerPageSize, filteredAccountManagerAccounts.length)} of {filteredAccountManagerAccounts.length}
              </SurfaceSubtext>
              <PageActionRow>
                <SecondaryButton
                  disabled={accountPage === 0}
                  onClick={() => setAccountPage(p => Math.max(0, p - 1))}
                  style={{ minHeight: 34 }}>
                  Previous
                </SecondaryButton>
                <SecondaryButton
                  disabled={accountPage >= managerPageCount - 1}
                  onClick={() => setAccountPage(p => Math.min(managerPageCount - 1, p + 1))}
                  style={{ minHeight: 34 }}>
                  Next
                </SecondaryButton>
              </PageActionRow>
            </PaginationRow>
          </>}
    </Surface>
  }

  const renderAccountDetailsModal = (): ReactElement | null => {
    if (!accountDetails) {
      return null
    }

    const account = accountDetails.account
    const meta = getManagedAccountMeta(accountDetails)
    const isDeposit = accountDetails.kind === 'deposit'
    const crypto = account.accountDetails.Crypto
    const fiat = account.accountDetails.Fiat
    const iban = fiat?.accountType.iban
    const us = fiat?.accountType.us
    const isIndividualIban = accountDetails.kind === 'withdraw' &&
      accountDetails.account.accountDetails.Fiat?.accountOwnerType?.toLowerCase() === 'individual' &&
      !!iban
    const fiatOwnerName = fiat
      ? 'accountOwnerName' in fiat
        ? fiat.accountOwnerName
        : iban?.accountHolderName ?? ('bankBeneficiaryName' in (us ?? {}) ? us?.bankBeneficiaryName : undefined) ?? '-'
      : '-'

    return <Modal
      visible={!!accountDetails}
      onClose={() => setAccountDetails(undefined)}
      title={getManagedAccountName(accountDetails)}
      innerStyles={{ width: 560 }}>
      <DetailRow style={{ borderTop: 0 }}>
        <DetailLabel>Type</DetailLabel>
        <DetailValue>{isDeposit ? accountDetails.rail === 'crypto' ? 'Deposit address' : 'Bank deposit account' : accountDetails.rail === 'crypto' ? 'Withdrawal wallet' : 'Bank withdrawal account'}</DetailValue>
      </DetailRow>
      <DetailRow>
        <DetailLabel>Status</DetailLabel>
        <DetailValue>{account.status === 'verfied' ? 'verified' : account.status}</DetailValue>
      </DetailRow>
      <DetailRow>
        <DetailLabel>Created</DetailLabel>
        <DetailValue>{formatCreatedAt(account.createdAt)}</DetailValue>
      </DetailRow>
      <DetailRow>
        <DetailLabel>Account ID</DetailLabel>
        <div style={{ alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
          <DetailValue style={{ fontFamily: 'var(--font-mono)' }}>{account.id}</DetailValue>
          <CopyButton text={account.id} />
        </div>
      </DetailRow>
      {crypto && <DetailRow>
        <DetailLabel>Address</DetailLabel>
        <div style={{ alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
          <DetailValue style={{ fontFamily: 'var(--font-mono)' }}>{crypto.address}</DetailValue>
          <CopyButton text={crypto.address} />
        </div>
      </DetailRow>}
      {crypto && isDeposit && <DetailRow>
        <DetailLabel>Supported assets</DetailLabel>
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
          {(crypto.depositMethods ?? []).map(method => {
            const managedMethod = getMethod(method.network, method.token)
            return <InlinePill key={`${method.network}-${method.token ?? 'native'}`}>
              {managedMethod.ticker} {managedMethod.hostChain?.name ?? managedMethod.name}
            </InlinePill>
          })}
        </div>
      </DetailRow>}
      {fiat && <DetailRow>
        <DetailLabel>Account owner</DetailLabel>
        <DetailValue>{fiatOwnerName}</DetailValue>
      </DetailRow>}
      {iban && <DetailRow>
        <DetailLabel>IBAN</DetailLabel>
        <div style={{ alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
          <DetailValue style={{ fontFamily: 'var(--font-mono)' }}>{iban.iban}</DetailValue>
          <CopyButton text={iban.iban} />
        </div>
      </DetailRow>}
      {iban && <DetailRow>
        <DetailLabel>BIC</DetailLabel>
        <div style={{ alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
          <DetailValue style={{ fontFamily: 'var(--font-mono)' }}>{iban.bic}</DetailValue>
          <CopyButton text={iban.bic} />
        </div>
      </DetailRow>}
      {us && <DetailRow>
        <DetailLabel>Account number</DetailLabel>
        <div style={{ alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
          <DetailValue style={{ fontFamily: 'var(--font-mono)' }}>{us.accountNumber}</DetailValue>
          <CopyButton text={us.accountNumber} />
        </div>
      </DetailRow>}
      {us && <DetailRow>
        <DetailLabel>Routing number</DetailLabel>
        <div style={{ alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
          <DetailValue style={{ fontFamily: 'var(--font-mono)' }}>{us.routingNumber}</DetailValue>
          <CopyButton text={us.routingNumber} />
        </div>
      </DetailRow>}
      {!crypto && !fiat && <RailHelper>{meta}</RailHelper>}
      <ModalFooter>
        <SecondaryButton onClick={() => setAccountDetails(undefined)}>Close</SecondaryButton>
        <PrimaryButton
          disabled={isIndividualIban}
          onClick={() => {
          selectManagedAccount(accountDetails)
          setAccountDetails(undefined)
        }}>
          {isIndividualIban ? 'Payout only' : isDeposit ? 'Use for deposit' : 'Use for withdrawal'}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  }

  const renderDepositPickerModal = (): ReactElement | null => {
    if (!depositPickerOpen) {
      return null
    }

    const query = depositPickerSearch.trim().toLowerCase()
    const filteredAccounts = visibleDepositAccounts.filter(account => {
      if (!query) return true
      const terms = [
        account.id,
        account.name,
        getDepositAccountMeta(account),
        account.customer?.name,
        account.customer?.email,
        account.customer?.id
      ]
      return terms.some(term => term?.toLowerCase().includes(query))
    })

    return <Modal
      visible={depositPickerOpen}
      onClose={() => setDepositPickerOpen(false)}
      title={depositRail === 'crypto' ? 'Choose deposit address' : 'Choose bank deposit account'}
      innerStyles={{ width: 560 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <SearchBox
          value={depositPickerSearch}
          onChange={e => setDepositPickerSearch(e.target.value)}
          placeholder='Search name, address, IBAN, customer, or ID'
          style={{ width: '100%' }}
        />
        {isLoadingAccounts
          ? <LoadingBar style={{ borderRadius: 20, height: 120, width: '100%' }} />
          : filteredAccounts.length === 0
            ? <EmptyState>
              {depositPickerSearch.trim()
                ? 'No accounts match this search.'
                : depositRail === 'crypto' ? 'No deposit addresses yet.' : 'No bank deposit accounts yet.'}
            </EmptyState>
            : <AccountList style={{ maxHeight: 360, overflow: 'auto' }}>
              {filteredAccounts.map(account => <AccountCard
                key={account.id}
                type='button'
                $active={account.id === selectedDepositAccount?.id}
                onClick={() => {
                  setSelectedDepositAccountId(account.id)
                  setDepositPickerOpen(false)
                }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                  <AccountName>{account.name}</AccountName>
                  <AccountMeta>{getDepositAccountMeta(account)}</AccountMeta>
                </div>
                <InlinePill>{formatDepositAccountType(account.accountDetails)}</InlinePill>
              </AccountCard>)}
            </AccountList>}
        <ModalFooter>
          <SecondaryButton onClick={() => setDepositPickerOpen(false)}>Close</SecondaryButton>
          {depositRail === 'crypto' && <PrimaryButton onClick={() => {
            setDepositPickerOpen(false)
            setCreateDepositOpen(true)
          }}>
            <ButtonLeadingIcon>+</ButtonLeadingIcon>
            <span>Create address</span>
          </PrimaryButton>}
        </ModalFooter>
      </div>
    </Modal>
  }

  const renderViewTabs = (): ReactElement => {
    const views: Array<{ key: FundingView, label: string }> = [
      { key: 'deposit', label: 'Deposit' },
      { key: 'withdraw', label: 'Withdraw' }
    ]

    return <UnderlineTabs role='tablist' aria-label='Funding workspace'>
      {views.map(view => <UnderlineTab
        key={view.key}
        type='button'
        role='tab'
        aria-selected={activeView === view.key}
        $active={activeView === view.key}
        onClick={() => updateView(view.key)}>
        {view.label}
      </UnderlineTab>)}
    </UnderlineTabs>
  }

  const renderOverview = (): ReactElement => (
    <Workspace>
      <OverviewGrid>
        <OverviewAction>
          <SurfaceHeader>
            <div>
              <SurfaceTitle>Deposit</SurfaceTitle>
              <SurfaceSubtext>Crypto addresses and SEPA funding accounts.</SurfaceSubtext>
            </div>
          </SurfaceHeader>
          <PageActionRow style={{ justifyContent: 'flex-start', marginTop: 'auto' }}>
            <PrimaryButton onClick={() => updateView('deposit')}>Deposit funds</PrimaryButton>
            <SecondaryButton onClick={() => openAccountWorkspace('depositCrypto')}>Manage addresses</SecondaryButton>
          </PageActionRow>
        </OverviewAction>
        <OverviewAction>
          <SurfaceHeader>
            <div>
              <SurfaceTitle>Withdraw</SurfaceTitle>
              <SurfaceSubtext>Send crypto or withdraw EUR to business IBANs.</SurfaceSubtext>
            </div>
          </SurfaceHeader>
          <PageActionRow style={{ justifyContent: 'flex-start', marginTop: 'auto' }}>
            <PrimaryButton onClick={() => updateView('withdraw')}>Withdraw funds</PrimaryButton>
            <SecondaryButton onClick={() => openAccountWorkspace('withdrawBank')}>Manage destinations</SecondaryButton>
          </PageActionRow>
        </OverviewAction>
        <OverviewAction>
          <SurfaceHeader>
            <div>
              <SurfaceTitle>Accounts</SurfaceTitle>
              <SurfaceSubtext>Saved deposit and withdrawal account details.</SurfaceSubtext>
            </div>
          </SurfaceHeader>
          <PageActionRow style={{ justifyContent: 'flex-start', marginTop: 'auto' }}>
            <PrimaryButton onClick={() => updateView('accounts')}>Open accounts</PrimaryButton>
          </PageActionRow>
        </OverviewAction>
      </OverviewGrid>
      {renderBalances()}
    </Workspace>
  )

  const renderStep = (
    index: number,
    title: string,
    hint: string,
    children: ReactElement,
    options: { complete?: boolean, active?: boolean, last?: boolean, action?: ReactElement } = {}
  ): ReactElement => (
    <StepRow>
      <StepRail>
        <StepMarker $complete={options.complete} $active={options.active}>
          {options.complete ? '✓' : index}
        </StepMarker>
        <StepConnector $hidden={options.last} />
      </StepRail>
      <StepBody>
        <StepHeader>
          <div>
            <StepTitle>{title}</StepTitle>
            <StepHint>{hint}</StepHint>
          </div>
          {options.action}
        </StepHeader>
        {children}
      </StepBody>
    </StepRow>
  )

  const renderCryptoDepositAssetOption = (asset?: CryptoDepositAssetOption): ReactElement => {
    if (!asset) {
      return <span style={{ color: '#747894', fontSize: 13 }}>Select asset</span>
    }

    const primaryOption = asset.options[0]
    const method = getMethod(primaryOption.network, primaryOption.token)

    return <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0 }}>
      <SimpleMethodIcon $logo={method.logo} $size={22} />
      <SelectionMeta>
        <SelectionLabel>{asset.ticker}</SelectionLabel>
        <SelectionSubtext>{asset.name}</SelectionSubtext>
      </SelectionMeta>
    </div>
  }

  const renderCryptoDepositNetworkOption = (option?: CryptoDepositOption): ReactElement => {
    if (!option) {
      return <span style={{ color: '#747894', fontSize: 13 }}>Select network</span>
    }

    const method = getMethod(option.network, option.token)
    const networkLabel = method.hostChain?.name ?? method.name
    const arrivalLabel = formatDepositNetworkArrival(networkLabel)

    return <NetworkOptionRow>
      <SimpleMethodIcon $logo={method.hostChain?.logo ?? method.logo} $size={22} />
      <SelectionMeta>
        <SelectionLabel>{networkLabel}</SelectionLabel>
        <SelectionSubtext>Estimated arrival</SelectionSubtext>
      </SelectionMeta>
      <ArrivalMeta>{arrivalLabel}</ArrivalMeta>
    </NetworkOptionRow>
  }

  const renderDepositAccountOption = (account?: DepositAccount): ReactElement => {
    if (!account) {
      return <span style={{ color: '#747894', fontSize: 13 }}>Select account</span>
    }

    return <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0, width: '100%' }}>
      <SelectionMeta>
        <SelectionLabel>{account.name}</SelectionLabel>
        <SelectionSubtext>{getDepositAccountMeta(account)}</SelectionSubtext>
      </SelectionMeta>
      <InlinePill>{formatDepositAccountType(account.accountDetails)}</InlinePill>
    </div>
  }

  const renderSelectedDepositAccount = (): ReactElement => {
    if (isLoadingAccounts) {
      return <LoadingBar style={{ borderRadius: 20, height: 58, width: '100%' }} />
    }

    if (depositRail === 'crypto') {
      if (cryptoDepositAssetOptions.length === 0) {
        return <EmptyState style={{ minHeight: 120 }}>
          <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span>No crypto deposit currencies yet.</span>
            <PrimaryButton onClick={() => setCreateDepositOpen(true)}>
              <ButtonLeadingIcon>+</ButtonLeadingIcon>
              <span>Create address</span>
            </PrimaryButton>
          </div>
        </EmptyState>
      }

      return <Dropdown
        matchTriggerWidth
        onClose={() => setDepositAssetSearch('')}
        outerStyle={{ minHeight: 44 }}
        selectedContent={renderCryptoDepositAssetOption(selectedCryptoDepositAsset)}
        dropdownContent={
          <DropdownContext.Consumer>
            {({ closeDropdown }) => <div style={{ minWidth: 0 }}>
              <DropdownSearchWrap>
                <CompactDropdownSearch
                  value={depositAssetSearch}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                  onChange={e => setDepositAssetSearch(e.target.value)}
                  placeholder='Search assets'
                  autoFocus />
              </DropdownSearchWrap>
              {filteredCryptoDepositAssetOptions.map(asset => <DropdownItem
                key={asset.ticker}
                onClick={() => {
                  setSelectedDepositAssetTicker(asset.ticker)
                  setSelectedDepositMethodKey(undefined)
                  setSelectedDepositAccountId(undefined)
                  closeDropdown()
                }}>
                {renderCryptoDepositAssetOption(asset)}
              </DropdownItem>)}
              {filteredCryptoDepositAssetOptions.length === 0 && <div style={{
                color: '#7d8492',
                fontSize: 12.5,
                lineHeight: 1.45,
                padding: '0 12px 14px'
              }}>
                No supported assets match this search.
              </div>}
            </div>}
          </DropdownContext.Consumer>
        } />
    }

    if (!selectedDepositAccount) {
      return <EmptyState style={{ minHeight: 120 }}>
        <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span>No bank deposit accounts yet.</span>
        </div>
      </EmptyState>
    }

    return <Dropdown
      matchTriggerWidth
      onClose={() => setDepositCashAccountSearch('')}
      outerStyle={{ minHeight: 44 }}
      selectedContent={renderDepositAccountOption(selectedDepositAccount)}
      dropdownContent={
        <DropdownContext.Consumer>
          {({ closeDropdown }) => <div>
            <DropdownSearchWrap>
              <CompactDropdownSearch
                value={depositCashAccountSearch}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
                onChange={e => setDepositCashAccountSearch(e.target.value)}
                placeholder='Search bank accounts'
                autoFocus />
            </DropdownSearchWrap>
            {searchedVisibleDepositAccounts.map(account => <DropdownItem
              key={account.id}
              onClick={() => {
                setSelectedDepositAccountId(account.id)
                closeDropdown()
              }}>
              {renderDepositAccountOption(account)}
            </DropdownItem>)}
            {searchedVisibleDepositAccounts.length === 0 && <div style={{
              color: '#7d8492',
              fontSize: 12.5,
              lineHeight: 1.45,
              padding: '0 12px 14px'
            }}>
              No bank accounts match this search.
            </div>}
          </div>}
        </DropdownContext.Consumer>
      } />
  }

  const renderSelectedDepositNetwork = (): ReactElement => {
    if (isLoadingAccounts) {
      return <LoadingBar style={{ borderRadius: 20, height: 58, width: '100%' }} />
    }

    if (!selectedCryptoDepositAsset) {
      return <EmptyState style={{ minHeight: 110 }}>Select an asset first.</EmptyState>
    }

    return <Dropdown
      matchTriggerWidth
      onClose={() => setDepositNetworkSearch('')}
      outerStyle={{ minHeight: 44 }}
      selectedContent={renderCryptoDepositNetworkOption(selectedCryptoDepositOption)}
      dropdownContent={
        <DropdownContext.Consumer>
          {({ closeDropdown }) => <div>
            <DropdownSearchWrap>
              <CompactDropdownSearch
                value={depositNetworkSearch}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
                onChange={e => setDepositNetworkSearch(e.target.value)}
                placeholder='Search networks'
                autoFocus />
            </DropdownSearchWrap>
            {filteredCryptoDepositNetworkOptions.map(option => <DropdownItem
              key={option.key}
              onClick={() => {
                setSelectedDepositMethodKey(option.key)
                setSelectedDepositAccountId(undefined)
                closeDropdown()
              }}>
              {renderCryptoDepositNetworkOption(option)}
            </DropdownItem>)}
            {filteredCryptoDepositNetworkOptions.length === 0 && <div style={{
              color: '#7d8492',
              fontSize: 12.5,
              lineHeight: 1.45,
              padding: '0 12px 14px'
            }}>
              No networks match this search.
            </div>}
          </div>}
        </DropdownContext.Consumer>
      } />
  }

  const renderSelectedDepositAddress = (): ReactElement => {
    if (isLoadingAccounts) {
      return <LoadingBar style={{ borderRadius: 20, height: 58, width: '100%' }} />
    }

    if (!selectedCryptoDepositOption) {
      return <EmptyState style={{ minHeight: 110 }}>Select an asset first.</EmptyState>
    }

    if (visibleDepositAccounts.length === 0) {
      return <EmptyState style={{ minHeight: 120 }}>
        <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span>No deposit address can currently receive this asset.</span>
          <PrimaryButton onClick={() => setCreateDepositOpen(true)}>
            <ButtonLeadingIcon>+</ButtonLeadingIcon>
            <span>Create address</span>
          </PrimaryButton>
        </div>
      </EmptyState>
    }

    return <Dropdown
      matchTriggerWidth
      onClose={() => setDepositAddressSearch('')}
      outerStyle={{ minHeight: 44 }}
      selectedContent={renderDepositAccountOption(selectedDepositAccount)}
      dropdownContent={
        <DropdownContext.Consumer>
          {({ closeDropdown }) => <div>
            <DropdownSearchWrap>
              <CompactDropdownSearch
                value={depositAddressSearch}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
                onChange={e => setDepositAddressSearch(e.target.value)}
                placeholder='Search deposit addresses'
                autoFocus />
            </DropdownSearchWrap>
            <DropdownItem
              onClick={() => {
                setCreateDepositOpen(true)
                closeDropdown()
              }}>
              <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0 }}>
                <ButtonLeadingIcon>+</ButtonLeadingIcon>
                <SelectionMeta>
                  <SelectionLabel>Create new address</SelectionLabel>
                  <SelectionSubtext>Add another deposit address for this asset.</SelectionSubtext>
                </SelectionMeta>
              </div>
            </DropdownItem>
            {searchedVisibleDepositAccounts.map(account => <DropdownItem
              key={account.id}
              onClick={() => {
                setSelectedDepositAccountId(account.id)
                closeDropdown()
              }}>
              {renderDepositAccountOption(account)}
            </DropdownItem>)}
            {searchedVisibleDepositAccounts.length === 0 && <div style={{
              color: '#7d8492',
              fontSize: 12.5,
              lineHeight: 1.45,
              padding: '0 12px 14px'
            }}>
              No deposit addresses match this search.
            </div>}
          </div>}
        </DropdownContext.Consumer>
      } />
  }

  const renderCashDepositMethodSelector = (): ReactElement => {
    const methods: Array<{
      key: DepositCashMethod
      title: string
      description: string
      pills: Array<{ label: string, tone?: 'green' | 'neutral' | 'blue' | 'amber' }>
    }> = [
      {
        key: 'sepaMemo',
        title: 'SEPA memo transfer',
        description: 'EUR bank transfer with a unique payment reference.',
        pills: [{ label: 'EUR', tone: 'blue' }, { label: 'Low cost', tone: 'green' }, { label: 'Coming soon', tone: 'amber' }]
      },
      {
        key: 'virtualAccount',
        title: 'Virtual account',
        description: 'Dedicated bank account details for repeat deposits.',
        pills: [{ label: 'Premium', tone: 'amber' }, { label: 'EUR, USD, MXN, BRL', tone: 'neutral' }]
      },
      {
        key: 'openBanking',
        title: 'Open banking',
        description: 'Bank payment initiated from the payer account.',
        pills: [{ label: 'Instant', tone: 'green' }, { label: 'Checkout flow', tone: 'neutral' }]
      }
    ]

    return <CashMethodGrid>
      {methods.map(method => <CashMethodCard
        key={method.key}
        type='button'
        $active={depositCashMethod === method.key}
        onClick={() => setDepositCashMethod(method.key)}>
        <CashMethodTitle>{method.title}</CashMethodTitle>
        <CashMethodDescription>{method.description}</CashMethodDescription>
        <CashMethodPillRow>
          {method.pills.map(pill => <InlinePill key={pill.label} $tone={pill.tone}>{pill.label}</InlinePill>)}
        </CashMethodPillRow>
      </CashMethodCard>)}
    </CashMethodGrid>
  }

  const renderCashDepositDetails = (): ReactElement => {
    if (depositCashMethod === 'sepaMemo') {
      return <RailHelper>
        SEPA memo-based deposits are coming soon. This will show one EUR account and a required payment reference once it is enabled.
      </RailHelper>
    }

    if (depositCashMethod === 'virtualAccount' && !virtualAccountsEnabled) {
      return <RailHelper>
        Virtual accounts are a premium funding option. Contact sales to enable dedicated bank account details for this organization.
      </RailHelper>
    }

    if (depositCashMethod === 'openBanking') {
      return <RailHelper>
        Direct open banking balance funding needs a merchant account-funding endpoint before we embed the bank flow here. Open banking remains available from checkout, invoice, and payment-link flows.
      </RailHelper>
    }

    return renderDepositAccountDetails()
  }

  const renderDepositWorkspace = (): ReactElement => (
    <FlowSurface>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>Deposit</SurfaceTitle>
          <SurfaceSubtext>Choose how funds are coming in, then copy the account details.</SurfaceSubtext>
        </div>
      </SurfaceHeader>
      <StepStack>
        {renderStep(
          1,
          'Select method',
          depositRail === 'crypto'
            ? 'Crypto deposits require a matching asset, network, and address.'
            : 'Choose how cash should move into Radom.',
          renderRailSelector(),
          { complete: true }
        )}
        {depositRail === 'crypto'
          ? <>
            {renderStep(
              2,
              'Choose asset',
              'Select the currency you want to receive.',
              renderSelectedDepositAccount(),
              { complete: !!selectedCryptoDepositAsset }
            )}
            {renderStep(
              3,
              'Choose network',
              'Select the network the sender will use.',
              renderSelectedDepositNetwork(),
              { complete: !!selectedCryptoDepositOption }
            )}
            {renderStep(
              4,
              'Choose address',
              'Pick the saved address that should receive this asset on this network.',
              renderSelectedDepositAddress(),
              { active: true, complete: !!selectedDepositAccount }
            )}
            {renderStep(
              5,
              'Deposit details',
              'Use the matching network and selected address.',
              renderDepositAccountDetails(),
              { last: true, active: !!selectedDepositAccount }
            )}
          </>
          : <>
            {renderStep(
              2,
              'Select cash deposit method',
              'Choose between SEPA memo transfers, virtual accounts, or open banking.',
              renderCashDepositMethodSelector(),
              { complete: true }
            )}
            {depositCashMethod === 'virtualAccount' && virtualAccountsEnabled && renderStep(
              3,
              'Choose virtual account',
              'Select the saved virtual account to show to the sender.',
              renderSelectedDepositAccount(),
              { active: true, complete: !!selectedDepositAccount }
            )}
            {renderStep(
              depositCashMethod === 'virtualAccount' && virtualAccountsEnabled ? 4 : 3,
              depositCashMethod === 'openBanking'
                ? 'Open banking deposit'
                : depositCashMethod === 'sepaMemo'
                  ? 'SEPA memo transfer'
                  : virtualAccountsEnabled ? 'Deposit details' : 'Contact sales',
              depositCashMethod === 'openBanking'
                ? 'Direct balance funding will use a dedicated account-funding endpoint.'
                : depositCashMethod === 'sepaMemo'
                  ? 'This deposit method is not available yet.'
                  : virtualAccountsEnabled ? 'Use the saved bank details for the selected virtual account.' : 'Enable virtual accounts before using this premium deposit rail.',
              renderCashDepositDetails(),
              { last: true, active: true }
            )}
          </>}
      </StepStack>
    </FlowSurface>
  )

  const renderWithdrawWorkspace = (): ReactElement => (
    <FlowSurface>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>Withdraw</SurfaceTitle>
          <SurfaceSubtext>Choose a withdrawal rail, destination, and amount.</SurfaceSubtext>
        </div>
      </SurfaceHeader>
      <StepStack>
        {renderStep(
          1,
          'Select method',
          withdrawalRail === 'bank'
            ? 'Cash withdrawals send to saved bank accounts.'
            : 'Crypto withdrawals send from your available network balance.',
          renderRailSelector(),
          { complete: true }
        )}
        {renderStep(
          2,
          withdrawalRail === 'bank' ? 'Set bank withdrawal' : 'Set crypto withdrawal',
          withdrawalRail === 'bank'
            ? 'Choose the cash currency, funding asset, destination, and amount.'
            : 'Choose a saved wallet destination and the amount to send.',
          <AssetWithdrawalModal
            key={withdrawalFormKey}
            modalOpen
            embedded
            hideWithdrawalKindSelector
            initialWithdrawalAccountId={selectedWithdrawalAccountId}
            initialWithdrawalKind={withdrawalRail === 'bank' ? 'cash' : 'crypto'}
            onWithdrawalKindChange={kind => setWithdrawalRail(kind === 'cash' ? 'bank' : 'crypto')}
            onCreateWithdrawalAccount={kind => openWithdrawalAccountModal(kind === 'cash' ? AccountType.Fiat : AccountType.Crypto)}
            onModalOpen={() => undefined}
            assetToWithdraw={withdrawableAssets[0] ?? { maxAmount: '', network: 'Ethereum' }}
            withdrawableAssets={withdrawableAssets}
          />,
          {
            active: true,
            last: true,
            action: <PrimaryButton
              onClick={() => openWithdrawalAccountModal(withdrawalRail === 'crypto' ? AccountType.Crypto : AccountType.Fiat)}
              style={{ minHeight: 34, whiteSpace: 'nowrap' }}>
              <ButtonLeadingIcon>+</ButtonLeadingIcon>
              <span>Add destination</span>
            </PrimaryButton>
          }
        )}
      </StepStack>
    </FlowSurface>
  )

  const renderActiveWorkspace = (): ReactElement => {
    if (activeView === 'withdraw') return renderWithdrawWorkspace()
    return renderDepositWorkspace()
  }

  return <Container>
    <DashboardPage>
      <PageHeaderRow>
        <PageHeader title='Funding' />
        <PageActionRow>
          <InlinePill $tone={testMode ? 'amber' : 'green'}>{testMode ? 'Test mode' : 'Live mode'}</InlinePill>
        </PageActionRow>
      </PageHeaderRow>

      <CenteredWorkspace>
        <FundingHero>
          <FundingTitle>{activeView === 'withdraw' ? 'Withdraw funds' : 'Deposit funds'}</FundingTitle>
          {renderViewTabs()}
        </FundingHero>

        {renderActiveWorkspace()}
        {activeView === 'withdraw' ? renderWithdrawalActivity() : renderDepositActivity()}
      </CenteredWorkspace>
    </DashboardPage>

    {createDepositOpen && <CreateDepositAddressModal
      title='Create deposit address'
      visible={createDepositOpen}
      onClose={() => setCreateDepositOpen(false)}
      onAccountCreated={() => {
        setCreateDepositOpen(false)
        loadFundingData()
      }}
    />}

    {createWithdrawalOpen && <CreateWithdrawalAccountModal
      title='Create withdrawal account'
      visible={createWithdrawalOpen}
      accountType={createWithdrawalType}
      onClose={() => setCreateWithdrawalOpen(false)}
      onAccountCreated={() => {
        setCreateWithdrawalOpen(false)
        loadFundingData()
        setWithdrawalFormKey(v => v + 1)
      }}
    />}
    {renderDepositPickerModal()}
    {renderAccountDetailsModal()}
    {exportDepositsOpen && <Modal
      visible={exportDepositsOpen}
      onClose={() => setExportDepositsOpen(false)}
      title='Export deposits'>
      <ExportDepositsForm />
    </Modal>}
    {exportWithdrawalsOpen && <Modal
      visible={exportWithdrawalsOpen}
      onClose={() => setExportWithdrawalsOpen(false)}
      title='Export withdrawals'>
      <ExportWithdrawalsForm />
    </Modal>}
  </Container>
})

export default Funding
